import React from "react";
import {
    alpha, Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    IconButton, LinearProgress, Paper, Stack, TextField,
    Tooltip, Typography,
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BuildIcon from "@mui/icons-material/BuildCircle";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ApiAnalyticsEngineCtrl from "../api/ApiAnalyticsEngineCtrl";
import {groupColor} from "../utils/groupColor";
import {dgStyle} from "./analyses/admin/utils/analyticsUtils";
import ModelEditDialog from "./analyses/admin/catalog/AnalyticsModelEditDialog";
import BuildLogsViewer from "./analyses/admin/catalog/BuildLogsViewer";
import { useToast } from "../context/ToastContext";
import {useAutoRefresh} from "../hooks/useAutoRefresh";
import RefreshControls from "../components/RefreshControls";

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildStatusColor(status) {
    if (!status) return "default";
    const s = status.toUpperCase();
    if (["SUCCESS", "COMPLETED", "OK"].includes(s))          return "success";
    if (["FAILED", "ERROR", "FAILURE"].includes(s))           return "error";
    if (["RUNNING", "BUILDING", "IN_PROGRESS"].includes(s))   return "info";
    if (["PENDING", "QUEUED"].includes(s))                    return "warning";
    return "default";
}

function extractStatus(data) {
    if (!data) return null;
    if (typeof data === "string") return data;
    return data.status ?? data.STATUS ?? null;
}

export default function PageAdminAnalyticsCatalog() {
    const [models,        setModels]        = React.useState([]);
    const [loading,       setLoading]       = React.useState(true);
    const [editTarget,    setEditTarget]    = React.useState(null);
    const [buildLogsModelId, setBuildLogsModelId] = React.useState(null);
    const [bulkOpen,      setBulkOpen]       = React.useState(false);
    const [bulkJson,      setBulkJson]       = React.useState("");
    const [bulkError,     setBulkError]      = React.useState(null);
    const [bulkImporting, setBulkImporting]  = React.useState(false);
    const [bulkProgress,  setBulkProgress]   = React.useState({done: 0, total: 0, errors: []});
    const bulkCancelRef = React.useRef(false);

    // Build statuses
    const [buildStatuses,    setBuildStatuses]     = React.useState({}); // modelId → raw data
    const [buildStsLoading,  setBuildStsLoading]   = React.useState(false);
    // K8s deploy statuses — modelId → {replicas, ...}
    const [deployStatuses,   setDeployStatuses]    = React.useState({});

    const [building, setBuilding] = React.useState(null);

    const { showToast } = useToast();

    const load = React.useCallback(async () => {
        setLoading(true);
        const {data: mdl} = await ApiAnalyticsEngineCtrl.getEngineModels();
        if (mdl) setModels(mdl);
        setLoading(false);

        // Charger les statuts de build en arrière-plan (ne bloque pas la grille)
        if (mdl) {
            setBuildStsLoading(true);
            const entries = await Promise.all(mdl.map(async m => {
                const {data} = await ApiAnalyticsEngineCtrl.getBuildStatus(m.id);
                return [m.id, data];
            }));
            setBuildStatuses(Object.fromEntries(entries.filter(([, d]) => d != null)));
            setBuildStsLoading(false);

            // Charger les statuts de déploiement K8s
            const {data: statuses} = await ApiAnalyticsEngineCtrl.getAllEngineStatuses();
            if (statuses) {
                setDeployStatuses(Object.fromEntries(
                    statuses.filter(s => s.modelId).map(s => [s.modelId, s])
                ));
            }
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const {autoRefresh, setAutoRefresh, intervalMs, setIntervalMs} = useAutoRefresh(load);

    const groups = React.useMemo(() => {
        const seen = new Map();
        models.forEach(m => {
            if (m.groupId && !seen.has(m.groupId))
                seen.set(m.groupId, {id: m.groupId, label: m.groupLabel ?? m.groupId});
        });
        return Array.from(seen.values());
    }, [models]);

    // ── Handlers ────────────────────────────────────────────────────────────────

    async function handleRebuild(model) {
        setBuilding(model.id);
        // Recharger le modèle complet (la liste peut ne pas avoir image.files)
        const {data: full} = await ApiAnalyticsEngineCtrl.getEngineModel(model.id);
        await ApiAnalyticsEngineCtrl.saveEngineModel(full || model);
        setBuilding(null);
        load();
    }

    async function handleDelete(id) {
        // Vérifier si le modèle a un déploiement K8s actif
        const {data: status} = await ApiAnalyticsEngineCtrl.getEngineStatus(id);
        if (status && status.replicas > 0) {
            showToast({msg: `Impossible de supprimer « ${id} » : il est actuellement déployé (${status.replicas} réplica${status.replicas > 1 ? "s" : ""}). Supprimez d'abord le déploiement K8s.`, severity: "error"});
            return;
        }
        await ApiAnalyticsEngineCtrl.deleteEngineModel(id);
        showToast({msg: `Modèle « ${id} » supprimé`, severity: "success"});
        load();
    }

    async function handleCopyJson(model) {
        try { await navigator.clipboard.writeText(JSON.stringify(model, null, 2)); } catch {}
    }

    function handleBulkOpen() {
        setBulkJson("");
        setBulkError(null);
        setBulkImporting(false);
        setBulkProgress({done: 0, total: 0, errors: []});
        bulkCancelRef.current = false;
        setBulkOpen(true);
    }

    /** Normalise un modèle pour l'API (même logique que ModelEditDialog.handleSave) */
    function normalizeModelForSave(model) {
        let img = model.image;
        // Convertir image string → objet
        if (typeof img === "string" && img) {
            const m = img.match(/^(.+?)\/(.+):(.+)$/);
            img = m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: img, tag: ""};
        } else if (!img || typeof img !== "object") {
            img = {registry: "", image: "", tag: ""};
        } else {
            img = {...img};
        }
        // Sans code source pour le bulk
        delete img.files;
        delete img.language;
        if (model.version && !img.tag) img.tag = model.version;
        return {
            ...model,
            nbSeries: model.series?.length ?? model.nbSeries ?? 1,
            image: img,
        };
    }

    async function handleBulkImport() {
        let parsed;
        try {
            parsed = JSON.parse(bulkJson);
            if (!Array.isArray(parsed)) {
                setBulkError("Le JSON doit être un tableau de modèles [ {...}, {...} ]");
                return;
            }
            if (parsed.length === 0) {
                setBulkError("Le tableau est vide");
                return;
            }
        } catch (e) {
            setBulkError("JSON invalide : " + e.message);
            return;
        }

        setBulkError(null);
        setBulkImporting(true);
        bulkCancelRef.current = false;
        const total = parsed.length;
        const errors = [];
        let done = 0;
        setBulkProgress({done: 0, total, errors: []});

        for (const model of parsed) {
            if (bulkCancelRef.current) break;
            const payload = normalizeModelForSave(model);
            const {error} = await ApiAnalyticsEngineCtrl.saveEngineModel(payload);
            done++;
            if (error) {
                errors.push(model.id || `index ${done}`);
            }
            setBulkProgress({done, total, errors: [...errors]});
        }

        const cancelled = bulkCancelRef.current;
        setBulkImporting(false);

        if (cancelled) {
            showToast({msg: `Import annulé — ${done} / ${total} traités`, severity: "info"});
            load();
        } else if (errors.length === 0) {
            showToast({msg: `${total} modèle${total > 1 ? "s" : ""} importé${total > 1 ? "s" : ""}`, severity: "success"});
            setBulkOpen(false);
            load();
        } else {
            showToast({msg: `${total - errors.length}/${total} importés, ${errors.length} erreur${errors.length > 1 ? "s" : ""}`, severity: "warning"});
            load();
        }
    }

    function handleClone(model) {
        const baseId = model.id.replace(/-v[\d.]+$/, "");
        const existingVersions = models
            .filter(m => m.id.replace(/-v[\d.]+$/, "") === baseId)
            .map(m => m.version || (typeof m.image === "object" ? m.image?.tag : "") || "0.0.0");
        let maxParts = [0, 0, 0];
        existingVersions.forEach(v => {
            const p = v.split(".").map(Number);
            for (let i = 0; i < 3; i++) {
                if ((p[i] || 0) > maxParts[i]) { maxParts = p.slice(0, 3); return; }
                if ((p[i] || 0) < maxParts[i]) return;
            }
        });
        maxParts[2] = (maxParts[2] || 0) + 1;
        const nextVersion = maxParts.join(".");
        const v = prompt(`Cloner "${model.id}" — nouvelle version :`, nextVersion);
        if (!v) return;
        const newId = baseId + "-v" + v;
        const {_rowId, ...data} = model;
        const img = typeof data.image === "object" && data.image ? {...data.image, tag: v} : {tag: v};
        setEditTarget({...data, id: newId, version: v, image: img});
    }

    // ── Colonnes ────────────────────────────────────────────────────────────────

    const columns = [
        {
            field: "groupId", headerName: "Groupe", width: 180,
            align: "left", headerAlign: "left",
            renderCell: ({row}) => {
                const label = row.groupLabel || row.groupId;
                if (!label) return <Typography variant="caption" color="text.disabled">—</Typography>;
                const gc = groupColor(row.groupId || "default");
                return (
                    <Chip label={label} size="small"
                          sx={{
                              color: gc,
                              backgroundColor: alpha(gc, 0.10),
                              border: `1.5px solid ${alpha(gc, 0.5)}`,
                              fontWeight: 700,
                              letterSpacing: 0.3,
                          }}/>
                );
            },
        },
        {
            field: "id", headerName: "Modele", flex: 2, minWidth: 200,
            align: "left", headerAlign: "left",
            renderCell: ({row}) => (
                <Stack justifyContent="center" sx={{height: "100%"}}>
                    <Typography variant="body2" fontWeight={700} noWrap title={row.id}>{row.id}</Typography>
                    {row.label && (
                        <Typography variant="caption" color="text.secondary" noWrap>{row.label}</Typography>
                    )}
                </Stack>
            ),
        },
        {
            field: "version", headerName: "Version", width: 90,
            align: "center", headerAlign: "center",
            renderCell: ({row}) => {
                const v = row.version || (typeof row.image === "object" ? row.image?.tag : "") || "";
                return v ? <Chip label={`v${v}`} size="small" variant="outlined" sx={{height: 20, fontSize: 10}}/> : "—";
            },
        },
        {
            field: "routingKey", headerName: "Routing Key", width: 150,
            valueGetter: (_v, row) => row.rabbitmq?.routingKey || row.id,
            renderCell: ({value}) => (
                <Typography variant="caption" fontFamily="monospace" fontSize={11} noWrap title={value}>{value}</Typography>
            ),
        },
        {
            field: "buildStatus", headerName: "Build", width: 110, sortable: false,
            align: "center", headerAlign: "center",
            renderCell: ({row}) => {
                if (buildStsLoading && !buildStatuses[row.id])
                    return <CircularProgress size={12} sx={{color: "text.disabled"}}/>;
                const s = extractStatus(buildStatuses[row.id]);
                if (!s) return <Chip label="—" size="small" variant="outlined" color="default"/>;
                return (
                    <Chip label={s} size="small" color={buildStatusColor(s)} variant="filled"
                          sx={{height: 20, fontSize: 10, fontWeight: 700}}/>
                );
            },
        },
        {
            field: "image", headerName: "Image Docker", flex: 2, minWidth: 280,
            renderCell: ({row}) => {
                const img = row.image;
                const str = typeof img === "object" && img
                    ? `${img.registry || ""}/${img.image || ""}:${img.tag || ""}`.replace(/^\//, "").replace(/:$/, "")
                    : (typeof img === "string" ? img : "—");
                return <Typography variant="caption" fontFamily="monospace" fontSize={11} noWrap title={str}>{str}</Typography>;
            },
        },
        {
            field: "actions", headerName: "Actions", width: 200, sortable: false, filterable: false,
            align: "center", headerAlign: "center",
            renderCell: ({row}) => {
                const deployed = deployStatuses[row.id]?.replicas > 0;
                return (
                <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center" sx={{width: "100%", height: "100%"}}>
                    <Tooltip title="Editer">
                        <IconButton size="small" sx={{color: "warning.main"}} onClick={() => setEditTarget(row)}>
                            <EditIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Rebuild image Docker (Kaniko)">
                        <IconButton size="small" sx={{color: "info.main"}} onClick={() => handleRebuild(row)}
                                    disabled={building === row.id}>
                            {building === row.id ? <CircularProgress size={14}/> : <BuildIcon fontSize="small"/>}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Cloner avec nouvelle version">
                        <IconButton size="small" color="primary" onClick={() => handleClone(row)}>
                            <FileCopyIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Copier JSON">
                        <IconButton size="small" onClick={() => handleCopyJson(row)}>
                            <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={deployed ? "Supprimez d'abord le deploiement K8s" : "Supprimer"}>
                        <span>
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}
                                        disabled={deployed}>
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Logs de build">
                        <IconButton size="small" color="info" onClick={() => setBuildLogsModelId(row.id)}>
                            <ArticleIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
                );
            },
        },
    ];

    return (
        <Box sx={{p: {xs: 2, md: 4}, maxWidth: 1800, mx: "auto"}}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <CollectionsBookmarkIcon color="primary" sx={{fontSize: 34}}/>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Collections moteurs d'analyses</Typography>
                        <Typography variant="body2" color="text.secondary">Catalogue des modeles d'analyse</Typography>
                    </Box>
                </Stack>

                <Stack direction="row" gap={0.5} alignItems="center">
                    <Tooltip title="Copier tout le JSON (pour import bulk)">
                        <IconButton onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(JSON.stringify(models, null, 2));
                                showToast({msg: `${models.length} modèle${models.length > 1 ? "s" : ""} copié${models.length > 1 ? "s" : ""} dans le presse-papier`, severity: "success"});
                            } catch {
                                showToast({msg: "Impossible de copier dans le presse-papier", severity: "error"});
                            }
                        }}>
                            <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Import bulk JSON">
                        <IconButton onClick={handleBulkOpen}>
                            <UploadFileIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Nouveau modèle">
                        <IconButton color="primary" onClick={() => setEditTarget("new")}>
                            <AddIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <RefreshControls
                        onRefresh={load}
                        refreshing={loading}
                        autoRefresh={autoRefresh}
                        onAutoRefreshChange={setAutoRefresh}
                        intervalMs={intervalMs}
                        onIntervalChange={setIntervalMs}
                    />
                </Stack>
            </Stack>

            {/* DataGrid */}
            <Paper elevation={1} sx={{borderRadius: 2, overflow: "hidden"}}>
                <DataGrid
                    rows={models}
                    columns={columns}
                    getRowId={r => r.id}
                    rowHeight={50}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {paginationModel: {pageSize: 25}},
                        sorting: {sortModel: [{field: "groupId", sort: "asc"}]},
                    }}
                    getRowClassName={(params) => params.row.groupId ? `row-group-${params.row.groupId}` : ""}
                    disableRowSelectionOnClick
                    disableColumnResize={false}
                    loading={loading}
                    sx={{
                        ...dgStyle(),
                        minHeight: 400,
                        ...Object.fromEntries(
                            [...new Set(models.map(m => m.groupId).filter(Boolean))].map((gid) => {
                                const gc = groupColor(gid);
                                return [`& .row-group-${gid}`, {
                                    backgroundColor: alpha(gc, 0.04),
                                    borderLeft: `3px solid ${alpha(gc, 0.6)}`,
                                    "&:hover": { backgroundColor: alpha(gc, 0.10) },
                                    "&.Mui-selected": { backgroundColor: alpha(gc, 0.16) },
                                }];
                            })
                        ),
                    }}
                />
            </Paper>

            {/* Dialog création / édition */}
            <ModelEditDialog
                target={editTarget}
                groups={groups}
                existingIds={models.map(m => m.id)}
                onClose={() => setEditTarget(null)}
                onSaved={load}
                showToast={showToast}
            />

            {/* Dialog logs de build */}
            <BuildLogsViewer
                open={!!buildLogsModelId}
                modelId={buildLogsModelId}
                onClose={() => setBuildLogsModelId(null)}
                showToast={showToast}
            />

            {/* Dialog bulk import */}
            <Dialog open={bulkOpen} onClose={() => !bulkImporting && setBulkOpen(false)}
                    maxWidth="md" fullWidth>
                <DialogTitle sx={{fontWeight: 700}}>
                    Import bulk de modèles
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        Collez un tableau JSON de modèles à importer. Chaque objet doit contenir au minimum un <code>id</code>.
                    </Typography>
                    <TextField
                        multiline
                        fullWidth
                        minRows={12}
                        maxRows={24}
                        placeholder={'[\n  { "id": "model-1", "label": "Mon modele", ... },\n  { "id": "model-2", ... }\n]'}
                        value={bulkJson}
                        onChange={(e) => { setBulkJson(e.target.value); setBulkError(null); }}
                        error={!!bulkError}
                        helperText={bulkError}
                        disabled={bulkImporting}
                        slotProps={{input: {sx: {fontFamily: "monospace", fontSize: 12}}}}
                    />

                    {bulkImporting && (
                        <Box sx={{mt: 2}}>
                            <Stack direction="row" justifyContent="space-between" sx={{mb: 0.5}}>
                                <Typography variant="caption" color="text.secondary">
                                    {bulkProgress.done} / {bulkProgress.total}
                                </Typography>
                                {bulkProgress.errors.length > 0 && (
                                    <Typography variant="caption" color="error">
                                        {bulkProgress.errors.length} erreur{bulkProgress.errors.length > 1 ? "s" : ""}
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}
                            />
                            {bulkProgress.errors.length > 0 && (
                                <Typography variant="caption" color="error" sx={{mt: 0.5, display: "block"}}>
                                    Échecs : {bulkProgress.errors.join(", ")}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <Divider/>
                <DialogActions sx={{px: 3, py: 1.5}}>
                    {bulkImporting ? (
                        <Button color="error" onClick={() => { bulkCancelRef.current = true; }}>
                            Arrêter
                        </Button>
                    ) : (
                        <Button onClick={() => setBulkOpen(false)}>
                            Fermer
                        </Button>
                    )}
                    <Button variant="contained" onClick={handleBulkImport}
                            disabled={bulkImporting || !bulkJson.trim()}
                            startIcon={bulkImporting ? <CircularProgress size={16}/> : <UploadFileIcon/>}>
                        {bulkImporting ? "Import en cours…" : "Importer"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
